import {
  assertArray,
  assertFiniteNumber,
  assertNonEmptyString,
  assertRecord,
  rejectUnknownKeys,
  validateEnum,
  validateOptionalString,
} from './elementFields.validation';

function validateCorridorPoints(value: unknown, label: string, minPoints: number): void {
  assertArray(value, label);
  if (value.length < minPoints) {
    throw new Error(`Invalid descriptor: ${label} needs at least ${minPoints} served point(s).`);
  }
  let previousX: number | undefined;
  value.forEach((point, index) => {
    const pointLabel = `${label}[${index}]`;
    assertRecord(point, pointLabel);
    rejectUnknownKeys(point, ['x', 'y'], pointLabel);
    assertFiniteNumber(point.x, `${pointLabel}.x`);
    assertFiniteNumber(point.y, `${pointLabel}.y`);
    // Published breakpoint series are keyed by strictly ascending height.
    // (Trajectory paths are validated separately and legitimately revisit an
    // x when a thinning drops basal area at the same height.)
    if (previousX !== undefined && point.x <= previousX) {
      throw new Error(
        `Invalid descriptor: ${pointLabel}.x must be strictly ascending — published breakpoint series never revisit a height.`,
      );
    }
    previousX = point.x;
  });
}

/**
 * A published band is a PAIR of published series on the same x grid. Anything
 * else — an upper series with its own grid, or a missing edge — would let a
 * band be fabricated between mismatched breakpoints.
 */
function validateCorridorBandPair(
  lower: readonly { x: number }[],
  upper: unknown,
  lowerLabel: string,
  upperLabel: string,
): void {
  validateCorridorPoints(upper, upperLabel, 2);
  const upperPoints = upper as readonly { x: number }[];
  if (upperPoints.length !== lower.length) {
    throw new Error(
      `Invalid descriptor: ${upperLabel} must pair ${lowerLabel} point-for-point (${lower.length} published breakpoints, got ${upperPoints.length}).`,
    );
  }
  upperPoints.forEach((point, index) => {
    if (point.x !== lower[index].x) {
      throw new Error(
        `Invalid descriptor: ${upperLabel}[${index}].x must equal ${lowerLabel}[${index}].x — a published band's edges share one x grid.`,
      );
    }
  });
}

function validateCorridorTrajectoryPaths(
  value: unknown,
  label: string,
  registerId: (id: unknown, idLabel: string) => void,
): void {
  assertArray(value, label);
  if (value.length > 2) {
    throw new Error(
      `Invalid descriptor: ${label} allows at most two paths (selected + reference).`,
    );
  }
  const roles = new Set<string>();
  value.forEach((path, index) => {
    const pathLabel = `${label}[${index}]`;
    assertRecord(path, pathLabel);
    rejectUnknownKeys(path, ['id', 'label', 'role', 'points', 'testId'], pathLabel);
    assertNonEmptyString(path.id, `${pathLabel}.id`);
    registerId(path.id, `${pathLabel}.id`);
    assertNonEmptyString(path.label, `${pathLabel}.label`);
    validateEnum(path.role, ['selected', 'reference'], `${pathLabel}.role`);
    if (roles.has(String(path.role))) {
      throw new Error(`Invalid descriptor: ${label} paths must have distinct roles.`);
    }
    roles.add(String(path.role));
    validateOptionalString(path, 'testId', pathLabel);
    assertArray(path.points, `${pathLabel}.points`);
    if (path.points.length === 0) {
      throw new Error(
        `Invalid descriptor: ${pathLabel}.points must carry at least one served state.`,
      );
    }
    path.points.forEach((point, pointIndex) => {
      const pointLabel = `${pathLabel}.points[${pointIndex}]`;
      assertRecord(point, pointLabel);
      rejectUnknownKeys(point, ['x', 'y', 'yearLabel', 'operationLabel'], pointLabel);
      assertFiniteNumber(point.x, `${pointLabel}.x`);
      assertFiniteNumber(point.y, `${pointLabel}.y`);
      assertNonEmptyString(point.yearLabel, `${pointLabel}.yearLabel`);
      validateOptionalString(point, 'operationLabel', pointLabel);
    });
  });
}

export function validateCorridorTrajectory(value: unknown, label: string): void {
  assertRecord(value, label);
  rejectUnknownKeys(
    value,
    [
      'xLabel',
      'yLabel',
      'xUnit',
      'yUnit',
      'subject',
      'rates',
      'refusals',
      'refusalsLabel',
      'deltaHeader',
      'statutoryFloor',
      'trajectories',
      'caption',
      'takeaway',
      'source',
      'emptyLabel',
      'testId',
    ],
    label,
  );
  ['xLabel', 'yLabel', 'xUnit', 'yUnit', 'caption', 'takeaway'].forEach((key) => {
    assertNonEmptyString(value[key], `${label}.${key}`);
  });
  ['refusalsLabel', 'deltaHeader', 'source', 'emptyLabel', 'testId'].forEach((key) =>
    validateOptionalString(value, key, label),
  );

  assertRecord(value.subject, `${label}.subject`);
  rejectUnknownKeys(value.subject, ['label', 'x', 'y'], `${label}.subject`);
  assertNonEmptyString(value.subject.label, `${label}.subject.label`);
  assertFiniteNumber(value.subject.x, `${label}.subject.x`);
  assertFiniteNumber(value.subject.y, `${label}.subject.y`);

  // One id namespace across rates, refusals, and paths: a corridor set is
  // either served (a rate) or refused (a refusal), never both, so a duplicate
  // id anywhere is a page wiring bug, not a legal reuse.
  const seenIds = new Set<string>();
  const registerId = (id: unknown, idLabel: string): void => {
    if (typeof id !== 'string') return; // shape errors are reported by the per-entry checks
    if (seenIds.has(id)) {
      throw new Error(
        `Invalid descriptor: ${idLabel} duplicates id "${id}" — rate, refusal, and path ids must be unique.`,
      );
    }
    seenIds.add(id);
  };

  assertArray(value.rates, `${label}.rates`);
  let enforcedCount = 0;
  value.rates.forEach((rate, index) => {
    const rateLabel = `${label}.rates[${index}]`;
    assertRecord(rate, rateLabel);
    rejectUnknownKeys(
      rate,
      [
        'id',
        'label',
        'emphasis',
        'emphasisLabel',
        'residual',
        'trigger',
        'residualUpper',
        'triggerUpper',
        'residualLabel',
        'triggerLabel',
        'deltaLabel',
        'testId',
      ],
      rateLabel,
    );
    ['id', 'label', 'residualLabel', 'triggerLabel'].forEach((key) => {
      assertNonEmptyString(rate[key], `${rateLabel}.${key}`);
    });
    registerId(rate.id, `${rateLabel}.id`);
    validateEnum(rate.emphasis, ['enforced', 'muted'], `${rateLabel}.emphasis`);
    if (rate.emphasis === 'enforced') enforcedCount += 1;
    ['emphasisLabel', 'deltaLabel', 'testId'].forEach((key) =>
      validateOptionalString(rate, key, rateLabel),
    );
    validateCorridorPoints(rate.residual, `${rateLabel}.residual`, 2);
    validateCorridorPoints(rate.trigger, `${rateLabel}.trigger`, 2);
    // A genuinely published band travels as PAIRED published series; a band
    // width scalar remains inexpressible (rejectUnknownKeys above).
    if (rate.residualUpper !== undefined) {
      validateCorridorBandPair(
        rate.residual as readonly { x: number }[],
        rate.residualUpper,
        `${rateLabel}.residual`,
        `${rateLabel}.residualUpper`,
      );
    }
    if (rate.triggerUpper !== undefined) {
      validateCorridorBandPair(
        rate.trigger as readonly { x: number }[],
        rate.triggerUpper,
        `${rateLabel}.trigger`,
        `${rateLabel}.triggerUpper`,
      );
    }
  });
  if (enforcedCount > 1) {
    throw new Error(`Invalid descriptor: ${label}.rates allows at most one enforced corridor.`);
  }

  if (value.refusals !== undefined) {
    assertArray(value.refusals, `${label}.refusals`);
    value.refusals.forEach((refusal, index) => {
      const refusalLabel = `${label}.refusals[${index}]`;
      assertRecord(refusal, refusalLabel);
      rejectUnknownKeys(refusal, ['id', 'label', 'reason', 'testId'], refusalLabel);
      ['id', 'label', 'reason'].forEach((key) =>
        assertNonEmptyString(refusal[key], `${refusalLabel}.${key}`),
      );
      registerId(refusal.id, `${refusalLabel}.id`);
      validateOptionalString(refusal, 'testId', refusalLabel);
    });
  }
  const refusalCount = Array.isArray(value.refusals) ? value.refusals.length : 0;
  if (value.rates.length === 0 && refusalCount === 0) {
    throw new Error(
      `Invalid descriptor: ${label} must carry at least one served corridor or one named refusal.`,
    );
  }

  assertRecord(value.statutoryFloor, `${label}.statutoryFloor`);
  const floor = value.statutoryFloor;
  rejectUnknownKeys(
    floor,
    ['label', 'detail', 'valueY', 'unavailableLabel'],
    `${label}.statutoryFloor`,
  );
  assertNonEmptyString(floor.label, `${label}.statutoryFloor.label`);
  validateOptionalString(floor, 'detail', `${label}.statutoryFloor`);
  const hasFloorValue = floor.valueY !== undefined;
  const hasFloorReason = floor.unavailableLabel !== undefined;
  if (hasFloorValue === hasFloorReason) {
    throw new Error(
      `Invalid descriptor: ${label}.statutoryFloor requires exactly one of valueY or unavailableLabel — a missing floor is named, never guessed.`,
    );
  }
  if (hasFloorValue) assertFiniteNumber(floor.valueY, `${label}.statutoryFloor.valueY`);
  if (hasFloorReason)
    assertNonEmptyString(floor.unavailableLabel, `${label}.statutoryFloor.unavailableLabel`);

  if (value.trajectories !== undefined) {
    validateCorridorTrajectoryPaths(value.trajectories, `${label}.trajectories`, registerId);
  }
}
